import React, { useState } from "react";
import { ArrowPathIcon, TrashIcon } from "@heroicons/react/24/outline";
import { AiOutlineSearch } from "react-icons/ai";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmationModal from "./ConfirmationModal";

const DeletedUserList = ({ deletedUsers, fetchUsers, setDeletedUsers }) => {
  const [isRestoreModalOpen, setRestoreModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const openRestoreModal = (userId) => {
    setSelectedUserId(userId);
    setRestoreModalOpen(true);
  };

  const openDeleteModal = (userId) => {
    setSelectedUserId(userId);
    setDeleteModalOpen(true);
  };

  const confirmRestoreUser = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/users/restore/${selectedUserId}`, {
        method: "PATCH",
      });
      if (!response.ok) throw new Error(`Error: ${response.status} - ${response.statusText}`);
      fetchUsers();
      setDeletedUsers((prev) => prev.filter((user) => user._id !== selectedUserId));
      toast.success("User restored successfully.");
    } catch (error) {
      console.error("Error restoring user:", error);
      toast.error("Failed to restore the user. Please try again.");
    } finally {
      setRestoreModalOpen(false);
      setIsLoading(false);
    }
  };

  const confirmPermanentDeleteUser = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/deleted-users/${selectedUserId}/permanent`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error(`Error: ${response.status} - ${response.statusText}`);
      fetchUsers();
      setDeletedUsers((prev) => prev.filter((user) => user._id !== selectedUserId));
      toast.success("User permanently deleted successfully.");
    } catch (error) {
      console.error("Error permanently deleting user:", error);
      toast.error("Failed to permanently delete the user. Please try again.");
    } finally {
      setDeleteModalOpen(false);
      setIsLoading(false);
    }
  };

  const filteredUsers = deletedUsers.filter(
    (user) =>
      (user.firstname && user.firstname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.lastname && user.lastname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h2 className="text-lg font-bold text-slate-950">Deleted Users</h2>
          <p className="text-sm text-slate-500">Restore temporarily removed users or permanently clear records.</p>
        </div>
        <label className="relative w-full lg:w-80">
          <AiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-slate-400" />
          <input
            type="text"
            placeholder="Search deleted users"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
          />
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-500">
                  No deleted users found.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user._id} className="text-slate-700 transition hover:bg-slate-50">
                  <td className="px-4 py-4 font-semibold text-slate-950">
                    {user.firstname} {user.lastname}
                  </td>
                  <td className="px-4 py-4 text-slate-500">{user.username}</td>
                  <td className="px-4 py-4">{user.role}</td>
                  <td className="px-4 py-4">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${
                        user.isPermanent
                          ? "bg-rose-50 text-rose-700 ring-rose-100"
                          : "bg-amber-50 text-amber-700 ring-amber-100"
                      }`}
                    >
                      {user.isPermanent ? "Permanent" : "Temporary"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openRestoreModal(user._id)}
                        className="inline-flex h-9 items-center gap-2 rounded-lg bg-sky-600 px-3 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isLoading}
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                        Restore
                      </button>
                      <button
                        onClick={() => openDeleteModal(user._id)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label="Permanent delete"
                        disabled={isLoading}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        onConfirm={confirmRestoreUser}
        message="Are you sure you want to restore this user?"
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmPermanentDeleteUser}
        message="Are you sure you want to permanently delete this user? This action cannot be undone."
      />

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
    </section>
  );
};

export default DeletedUserList;
